import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as MarketActions from 'app/main/marketplace/store/actions';
import * as ActionsDialog from 'app/store/actions';

export default function useMarketplace({ provider, form, setField, propsMarketId }) {
    const dispatch = useDispatch();
    const marketplaceData = useSelector(({ marketStack }) => marketStack.marketStack.list);
    const selectedMarketStack = useSelector(({ marketStack }) => marketStack.marketStack.stack);
    const globalProvisionPreview = useSelector(({ globalProvision }) => globalProvision?.preview);

    const marketId = selectedMarketStack?.id || propsMarketId;
    const isMarketStack = Boolean(selectedMarketStack);
    const currentMarketStack = marketplaceData?.find(item => item.id === marketId) || selectedMarketStack;

    const title = currentMarketStack
        ? `${currentMarketStack.name} ${currentMarketStack.version}`
        : form?.hostname || 'New Cluster';
    const imageSrc = currentMarketStack?.logo
        ? `assets/images/logos/${currentMarketStack.logo}`
        : `assets/images/providers/${provider}.png`;

    // ====================================================================
    // Variant resolution
    // --------------------------------------------------------------------
    // A marketplace entry can declare `variants: [{ arch, os, version, config }]`.
    // When present, the user picks (arch, os) in the form and the variant's
    // `config` is merged on top of the product-level `config`. Entries without
    // `variants[]` keep the historical flat behavior (entry.config is used as-is).
    // ====================================================================
    const variants = currentMarketStack?.variants;
    const hasVariants = Array.isArray(variants) && variants.length > 0;

    const availableArchs = useMemo(
        () => (hasVariants ? Array.from(new Set(variants.map(v => v.arch))) : null),
        [variants, hasVariants]
    );

    const availableOsesForArch = useCallback(
        (arch) => (hasVariants ? variants.filter(v => v.arch === arch).map(v => v.os) : null),
        [variants, hasVariants]
    );

    // Default selection: first variant when the user hasn't picked one yet.
    const selectedArch = hasVariants
        ? (form?.marketArch || variants[0].arch)
        : null;
    const selectedOs = hasVariants
        ? (form?.marketOs
            || variants.find(v => v.arch === selectedArch)?.os
            || variants[0].os)
        : null;
    const selectedVariant = hasVariants
        ? (variants.find(v => v.arch === selectedArch && v.os === selectedOs) || variants[0])
        : null;

    // activeConfig = product-level config + selected variant's config (variant wins).
    // Returned as `config` so every downstream consumer keeps working unchanged.
    const config = useMemo(() => {
        if (!currentMarketStack) return null;
        if (!hasVariants) return currentMarketStack.config || null;
        return { ...(currentMarketStack.config || {}), ...(selectedVariant?.config || {}) };
    }, [currentMarketStack, hasVariants, selectedVariant]);

    const setArch = useCallback((arch) => {
        if (!hasVariants) return;
        const oses = variants.filter(v => v.arch === arch).map(v => v.os);
        const nextOs = (form?.marketOs && oses.includes(form.marketOs)) ? form.marketOs : oses[0];
        setField({ marketArch: arch, marketOs: nextOs });
    }, [hasVariants, variants, form?.marketOs, setField]);

    const setOs = useCallback((os) => {
        if (!hasVariants) return;
        setField('marketOs', os);
    }, [hasVariants, setField]);

    // Persist the default variant into form state once a marketplace stack is selected.
    useEffect(() => {
        if (!hasVariants || !form) return;
        if (!form.marketArch || !form.marketOs) {
            setField({ marketArch: selectedArch, marketOs: selectedOs });
        }
    }, [hasVariants, form, selectedArch, selectedOs, setField]);

    // When the selected variant changes, clear instance picks that are no longer
    // in the variant's allow-list, sync osType, and raise volume to min_volume.
    useEffect(() => {
        if (!hasVariants || !selectedVariant || !form) return;
        const vcfg = selectedVariant.config || {};
        const allowed = vcfg.instance_type || vcfg.computeNodeSize || null;
        const updates = {};

        if (allowed && form.serverInstanceId && !allowed.includes(form.serverInstanceId)) {
            updates.serverInstanceId = '';
        }
        if (Array.isArray(form.nodeGroups) && allowed) {
            let changed = false;
            const fixed = form.nodeGroups.map((g) => {
                if (g && g.instance_id && !allowed.includes(g.instance_id)) {
                    changed = true;
                    return { ...g, instance_id: '' };
                }
                return g;
            });
            if (changed) updates.nodeGroups = fixed;
        }
        if (vcfg.os_types && form.osType !== vcfg.os_types) {
            updates.osType = vcfg.os_types;
        }
        if (vcfg.min_volume) {
            if (!form.serverVolumeSize || Number(form.serverVolumeSize) < Number(vcfg.min_volume)) {
                updates.serverVolumeSize = vcfg.min_volume;
            }
            // Compute nodes run the SAME (large) marketplace image as the head, so
            // raise their disk to the same floor — otherwise a fresh marketplace
            // deploy's compute nodes get a too-small default and fill up (the E4S
            // image alone is ~180GB).
            if (!form.nodeVolumeSize || Number(form.nodeVolumeSize) < Number(vcfg.min_volume)) {
                updates.nodeVolumeSize = vcfg.min_volume;
            }
        }
        if (Object.keys(updates).length > 0) setField(updates);
    }, [hasVariants, selectedVariant, form, setField]);

    // Clean up marketplace state when form unmounts
    useEffect(() => {
        return () => dispatch(MarketActions.clearStack());
    }, [dispatch]);

    // Manager override (marketplace data may load after form init)
    useEffect(() => {
        if (isMarketStack && config?.resource_manager && form && form.manager !== config.resource_manager) {
            setField('manager', config.resource_manager);
        }
    }, [isMarketStack, config?.resource_manager, form?.manager, setField]);

    // Auto-exclude all global scripts when marketplace config requires it
    useEffect(() => {
        if (config?.disable_global_scripts && globalProvisionPreview?.configs?.length > 0) {
            const allScriptIds = [];
            globalProvisionPreview.configs.forEach(c => {
                if (c.scripts) c.scripts.forEach(s => allScriptIds.push(`${c._id}-${s._id}`));
            });
            if (allScriptIds.length > 0) setField('excludedGlobalScripts', allScriptIds);
        }
    }, [config?.disable_global_scripts, globalProvisionPreview?.configs, setField]);

    // Download provision script from marketplace URL (if configured)
    useEffect(() => {
        if (!config?.provision_script || !form || form.provisionScript) return;
        let cancelled = false;
        (async () => {
            try {
                dispatch(ActionsDialog.toggleLoading());
                const scriptContent = await dispatch(MarketActions.getProvisionScript(config.provision_script));
                if (!cancelled && scriptContent) setField('provisionScript', scriptContent);
            } catch (err) {
                console.error('Error downloading provision script:', err);
            } finally {
                dispatch(ActionsDialog.toggleLoading());
            }
        })();
        return () => { cancelled = true; };
    }, [config?.provision_script, form?.provisionScript, dispatch, setField]);

    // Pre-populate applications from marketplace config
    useEffect(() => {
        if (isMarketStack && config?.applications?.length > 0 && !(form?.applications?.length > 0)) {
            setField('applications', config.applications.map((app, i) => ({
                id: app.id || `${currentMarketStack.id || 'app'}-${i}`,
                name: app.name || '',
                url: app.url || '',
                icon: app.icon || 'apps',
                description: app.description || '',
                category: app.category || 'general',
                launchType: app.launchType || 'url',
                instructions: app.instructions || '',
                enabled: app.enabled !== false,
                actionType: app.actionType || 'access-tab',
                actionButtonLabel: app.actionButtonLabel || '',
            })));
        }
    }, [isMarketStack, config?.applications, form?.applications, setField, currentMarketStack?.id]);

    // OS picker filter: with variants, restrict to OSes available for the selected arch.
    // Without variants, preserve the historical contract (string or null).
    const allowedOsTypes = hasVariants
        ? (availableOsesForArch(selectedArch) || null)
        : (config?.os_types || null);

    return {
        marketId,
        isMarketStack,
        title,
        imageSrc,
        config,
        disableStackFields: isMarketStack,
        allowedOsTypes,
        allowedComputeIds: config?.computeNodeSize || null,
        hideProvisionScript: config?.hide_provision_script === true,
        disableGlobalScripts: config?.disable_global_scripts === true,
        // Variant API (null/false when the entry has no variants[])
        variants: hasVariants ? variants : null,
        hasVariants,
        availableArchs,
        availableOsesForArch,
        selectedArch,
        selectedOs,
        setArch,
        setOs,
    };
}
